import { useCallback, useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";

export default function useBlackjackDealerDisplayTable(tableId, displayToken = "") {
  const [state, setState] = useState(null);
  const [transport, setTransport] = useState("polling");
  const [error, setError] = useState("");
  const [lastUpdateAt, setLastUpdateAt] = useState(0);
  const alive = useRef(true);
  const revisionRef = useRef(-1);

  const apply = useCallback((next) => {
    if (!next) return;
    const revision = Number(next.revision ?? 0);
    if (revision <= revisionRef.current) return;
    revisionRef.current = revision;
    setState(next);
    setLastUpdateAt(Date.now());
    setError("");
  }, []);

  const poll = useCallback(async () => {
    if (!tableId) return;
    if (displayToken) {
      const { data } = await api.get(
        `/casino/bj/dealer-display-state?table=${tableId}`,
        { headers: { "X-Dealer-Display-Token": displayToken } },
      );
      if (alive.current) {
        // Liveness independent of revision: an idle table is still connected, so
        // don't falsely tell the dealer to pause when nothing has changed.
        setLastUpdateAt(Date.now());
        apply(data);
      }
      return;
    }

    const { data } = await api.get(`/admin/bj/state?table=${tableId}`);
    if (alive.current) {
      setLastUpdateAt(Date.now());
      apply(data);
    }
  }, [apply, displayToken, tableId]);

  useEffect(() => {
    alive.current = true;
    revisionRef.current = -1;
    setState(null);
    setLastUpdateAt(0);
    setError("");
    setTransport("polling");
    if (!tableId) return () => { alive.current = false; };

    let ws;
    let retry;
    let heartbeat;
    let stopped = false;

    poll().catch((err) => {
      if (alive.current) setError(err?.response?.data?.detail || "Dealer display link is unavailable");
    });

    const connect = async () => {
      try {
        const wsBase = API.replace(/^http:/, "ws:").replace(/^https:/, "wss:");
        let query;
        if (displayToken) {
          query = `display_token=${encodeURIComponent(displayToken)}`;
        } else {
          const { data } = await api.post(`/casino/bj/socket-ticket?table=${tableId}`);
          query = `ticket=${encodeURIComponent(data.ticket)}`;
        }

        ws = new WebSocket(`${wsBase}/ws/blackjack/${tableId}?${query}`);
        ws.onopen = () => {
          if (stopped) return;
          setTransport("realtime");
          setError("");
          clearInterval(heartbeat);
          heartbeat = setInterval(() => {
            if (ws?.readyState === WebSocket.OPEN) ws.send("ping");
          }, 20000);
        };
        ws.onmessage = (event) => {
          try {
            const packet = JSON.parse(event.data);
            if (packet?.type === "state") {
              setLastUpdateAt(Date.now());
              apply(packet.state);
            }
          } catch {}
        };
        ws.onerror = () => {
          if (!stopped) setTransport("polling");
        };
        ws.onclose = () => {
          clearInterval(heartbeat);
          if (stopped) return;
          setTransport("polling");
          retry = setTimeout(connect, 2500);
        };
      } catch (err) {
        if (stopped) return;
        setTransport("polling");
        if (displayToken && !state) setError(err?.response?.data?.detail || "Dealer display link is unavailable");
        retry = setTimeout(connect, 3000);
      }
    };

    connect();
    const fallback = setInterval(() => {
      poll().catch((err) => {
        if (alive.current) setError(err?.response?.data?.detail || "Dealer display link is unavailable");
      });
    }, 4000);

    return () => {
      alive.current = false;
      stopped = true;
      clearInterval(fallback);
      clearInterval(heartbeat);
      clearTimeout(retry);
      try { ws?.close(); } catch {}
    };
  }, [apply, displayToken, poll, tableId]);

  return { state, transport, error, lastUpdateAt };
}
